import {
  NOTIFICATION_UNSEEN_COUNT_EXCLUDE_TYPES,
  NotificationIntent,
  NotificationType
} from '@constants'
import { useCurrentUser } from '@query'
import { network } from '@utils'
import { useQuery } from '@tanstack/react-query'
import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from './root'

interface ISimpleBusiness {
  id: number
  business_id: string
  name: string
  slug: string
}

export interface INotification2 {
  id: number
  type: NotificationType
  seen: boolean
  intent: NotificationIntent
  timestamp: string

  business?: ISimpleBusiness

  conversation_id?: string

  custom_message_title?: string
  custom_message_content?: string
  custom_message_link?: string

  related_object_id?: number
  related_object_ct?: string
}

interface IInvitationData {
  invitation_id: number
  business: number
  business_name: string
}

export interface INotification {
  action: string
  data: IInvitationData
}

interface NotificationListProps {
  slug?: string
}

export const fetchNotifications: QueryFunction<
  NotificationListProps,
  PaginatedResponse<INotification2>
> = async ({ slug }, params) => {
  const url = slug
    ? `/v1/notification/in_app/notifications/business/${slug}/`
    : `/v1/notification/in_app/notifications/`

  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

interface INotificationUnseenCounts {
  count_user_related: number
  count_business_related: number
  counts_by_business: { [slug: number]: number }
}

export const notificationUnseenCountsQueryKeySegment = 'unseen_counts'

export const fetchNotificationUnseenCounts = async (
  excludeTypes: readonly NotificationType[] = NOTIFICATION_UNSEEN_COUNT_EXCLUDE_TYPES
): Promise<INotificationUnseenCounts> => {
  const params = new URLSearchParams()
  for (const t of excludeTypes) {
    params.append('exclude_type', t)
  }
  const qs = params.toString()
  const url = qs
    ? `/v1/notification/in_app/unseen_counts/?${qs}`
    : `/v1/notification/in_app/unseen_counts/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

interface NotificationInstanceProps {
  notificationId: number
}

export const updateNotification: MutationFunction<
  NotificationInstanceProps,
  INotification2
> = async ({ notificationId }, requestData) => {
  const url = `/v1/notification/in_app/notifications/${notificationId}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const useUnseenNotificationCount = (slug?: number): number => {
  const { data: user } = useCurrentUser()
  const excludeTypes = NOTIFICATION_UNSEEN_COUNT_EXCLUDE_TYPES
  const excludeKey = [...excludeTypes].slice().sort()
  const { data: counts } = useQuery({
    queryKey: ['notifications', user?.id, notificationUnseenCountsQueryKeySegment, excludeKey],
    queryFn: () => fetchNotificationUnseenCounts(excludeTypes),
    ...{
      enabled: !!user?.id
    }
  })

  return slug
    ? (counts?.count_user_related || 0) + (counts?.counts_by_business?.[slug] || 0)
    : (counts?.count_user_related || 0) + (counts?.count_business_related || 0)
}

export const markAllAsSeen: MutationFunction<NotificationListProps, void> = async ({ slug }) => {
  const url = slug
    ? `/v1/notification/in_app/mark_as_seen/business/${slug}/`
    : `/v1/notification/in_app/mark_as_seen/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: {},
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const NOTIFICATION_CATEGORY_KEYS = [
  'invoicing',
  'vat_reporting',
  'banking',
  'files_and_imports',
  'comments',
  'travel_and_expenses',
  'system'
] as const

export type NotificationCategory = (typeof NOTIFICATION_CATEGORY_KEYS)[number]

export const NOTIFICATION_CHANNELS = ['push', 'email'] as const

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]

export const NOTIFICATION_CHANNEL_FIELD = {
  push: 'categories',
  email: 'email_categories'
} as const

export const ACCOUNTING_SUGGESTIONS_DIGEST_FREQUENCIES = ['daily', 'weekly', 'monthly'] as const

export type AccountingSuggestionsDigestFrequency =
  (typeof ACCOUNTING_SUGGESTIONS_DIGEST_FREQUENCIES)[number]

export interface IUsershipNotificationPreferences {
  categories: Record<NotificationCategory, boolean>
  email_categories: Record<NotificationCategory, boolean>
  accounting_suggestions_digest_frequency: AccountingSuggestionsDigestFrequency
}

export const isAccountingSuggestionsDigestFrequency = (
  value: string
): value is AccountingSuggestionsDigestFrequency =>
  value === 'daily' || value === 'weekly' || value === 'monthly'

export const mergeNotificationCategoryDefaults = (
  stored?: Record<string, boolean> | null
): Record<NotificationCategory, boolean> => {
  const base = Object.fromEntries(NOTIFICATION_CATEGORY_KEYS.map(key => [key, true])) as Record<
    NotificationCategory,
    boolean
  >
  if (!stored) {
    return base
  }
  for (const [key, raw] of Object.entries(stored)) {
    if (key in base && typeof raw === 'boolean') {
      base[key as NotificationCategory] = raw
    }
  }
  return base
}

const parseUsershipNotificationPreferences = (data: {
  categories?: Record<string, boolean>
  email_categories?: Record<string, boolean>
  accounting_suggestions_digest_frequency?: string | null
}): IUsershipNotificationPreferences => {
  const frequency = data.accounting_suggestions_digest_frequency ?? ''
  return {
    categories: mergeNotificationCategoryDefaults(data.categories),
    email_categories: mergeNotificationCategoryDefaults(data.email_categories),
    accounting_suggestions_digest_frequency: isAccountingSuggestionsDigestFrequency(frequency)
      ? frequency
      : 'weekly'
  }
}

export const fetchUsershipNotificationPreferences: QueryFunction<
  { slug: string },
  IUsershipNotificationPreferences
> = async ({ slug }) => {
  const url = `/v1/notification/in_app/push_preferences/business/${slug}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return parseUsershipNotificationPreferences(data)
}

export const updateUsershipNotificationPreferences: MutationFunction<
  { slug: string },
  IUsershipNotificationPreferences
> = async ({ slug }, requestData) => {
  const url = `/v1/notification/in_app/push_preferences/business/${slug}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return parseUsershipNotificationPreferences(handleReturn({ status, data }))
}
